import moment from 'moment';
import { t } from '../../config/i18n';
import { DateFormik, Input, TelInput } from '../../components/common';
import { DateFormats } from '../Dates';
import { isTupayPanel } from '../panelUtils';
import { phoneInputPropsTupay } from '.';
import { ErrorMessage } from '../../components/PaymentOrders/layouts/ErrorMessage';
import { getTimezoneOffsetHours } from '../Functions';

const getValidStatusList = (statusList, statusMapping) => {
  if (!Array.isArray(statusList)) return undefined;

  const validStatuses = statusList
    .map(status => Object.keys(statusMapping).find(key => statusMapping[key] === status))
    .filter(status => status !== undefined)
    .join(',');

  return validStatuses || undefined;
};

const transformFiltersForExport = (filters, statusMapping) => {
  const transformedFilters = { ...filters };

  transformedFilters.status = getValidStatusList(filters.status, statusMapping);
  if (!transformedFilters.status) delete transformedFilters.status;

  if (transformedFilters.descripcion) {
    transformedFilters.id = transformedFilters.descripcion;
    delete transformedFilters.descripcion;
  }

  return transformedFilters;
};

export const prepareExportFilters = (filters, statusMapping) => ({
  ...transformFiltersForExport(filters, statusMapping),
  gmtOffset: getTimezoneOffsetHours()
});

export const columnBatchVisibility = {
  id: true,
  merchantId: true,
  submissionDate: true,
  totalTransactions: true,
  status: true,
};

export const columnVisibilityOrder = {
  id: true,
  creationDate: true,
  concept: true,
  clientEmail: true,
  initialAmount: true,
  latePaymentAmount: true,
  daysLate: true,
  amount: true,
  status: true,
  dueDate: true,
  expiration: true,
  paymentReference: true,
  batchId: true,
  clientDocument: true,
  description: false,
  currency: false,
  clientName: false,
  documentType: false,
  phone: false,
  clientLastName: false,

};

export const csvHeaders = [
  'columnPaymentReference',
  'columnConcept',
  'labelDescription',
  'columnCurrency',
  'labelAmount',
  'columnDueDate',
  'columnLatePaymentAmount',
  'labelExpiration',
  'labelClientName',
  'labelClientLastName',
  'labelDocumentType',
  'columnClientDocument',
  'columnClientEmail',
  'labelPhoneNumber'
];

export const csvHeadersMap = {
  paymentReference: 'columnPaymentReference',
  concept: 'columnConcept',
  description: 'labelDescription',
  currency: 'columnCurrency',
  amount: 'labelAmount',
  dueDate: 'columnDueDate',
  latePaymentAmount: 'columnLatePaymentAmount',
  expiration: 'labelExpiration',
  clientName: 'labelClientName',
  clientLastName: 'labelClientLastName',
  documentType: 'labelDocumentType',
  clientDocument: 'columnClientDocument',
  clientEmail: 'columnClientEmail',
  phone: 'labelPhoneNumber',
};

export const statusMappingBatch = {
  0: 'CREATED',
  1: 'PENDING',
  2: 'CONFIRMED',
  3: 'PROCESSING',
  4: 'COMPLETED',
  5: 'CANCELLED',
};

export const statusMappingOrder = {
  0: 'CREATED',
  1: 'TO_REVIEW',
  2: 'PENDING',
  3: 'PROCESSING_PAYMENT',
  4: 'PAID',
  5: 'CANCELLED',
  6: 'EXPIRED',
  7: 'REJECTED',
};

export const formFields = () => {
  const telInputProps = isTupayPanel
    ? phoneInputPropsTupay
    : {};

  return [
    {
      key: 'paymentReference',
      component: Input,
      label: t(csvHeadersMap.paymentReference),
      props: {
        placeholder: t(csvHeadersMap.paymentReference),
        required: true,
      }
    },
    {
      key: 'concept',
      component: Input,
      label: t(csvHeadersMap.concept),
      props: {
        placeholder: t(csvHeadersMap.concept),
        required: true,
      }
    },
    {
      key: 'description',
      component: Input,
      label: t(csvHeadersMap.description),
      props: {
        placeholder: t(csvHeadersMap.description),
        required: true,
      }
    },
    {
      key: 'currency',
      component: Input,
      label: t(csvHeadersMap.currency),
      props: {
        placeholder: t(csvHeadersMap.currency),
        required: true,
      }
    },
    {
      key: 'amount',
      component: Input,
      label: t(csvHeadersMap.amount),
      props: {
        placeholder: t(csvHeadersMap.amount),
        type: 'number',
        required: true,
      }
    },
    {
      key: 'dueDate',
      component: DateFormik,
      label: t(csvHeadersMap.dueDate),
      props: {
        resultFormat: DateFormats.StandardEuropeanDateFormat,
        maxDate: moment().add(12, 'months'),
        dateFormat: DateFormats.RegularDate,
        singleDatePicker: true,
        timePicker: false,
        placeholder: t(csvHeadersMap.dueDate),
        required: true,
        CustomError: ErrorMessage,
      }
    },
    {
      key: 'latePaymentAmount',
      component: Input,
      label: t(csvHeadersMap.latePaymentAmount),
      props: {
        placeholder: t(csvHeadersMap.latePaymentAmount),
        type: 'number',
        required: true,
      }
    },
    {
      key: 'expiration',
      component: DateFormik,
      label: t(csvHeadersMap.expiration),
      props: {
        resultFormat: DateFormats.StandardEuropeanDateFormat,
        maxDate: moment().add(12, 'months'),
        dateFormat: DateFormats.RegularDate,
        timePicker: false,
        singleDatePicker: true,
        placeholder: t(csvHeadersMap.expiration),
        required: true,
        CustomError: ErrorMessage,
      }
    },
    {
      key: 'clientName',
      component: Input,
      label: t(csvHeadersMap.clientName),
      props: {
        placeholder: t(csvHeadersMap.clientName),
        required: true,
      }
    },
    {
      key: 'clientLastName',
      component: Input,
      label: t(csvHeadersMap.clientLastName),
      props: {
        placeholder: t(csvHeadersMap.clientLastName),
        required: true,
      }
    },
    {
      key: 'documentType',
      component: Input,
      label: t(csvHeadersMap.documentType),
      props: {
        placeholder: t(csvHeadersMap.documentType),
        required: true,
      }
    },
    {
      key: 'clientDocument',
      component: Input,
      label: t(csvHeadersMap.clientDocument),
      props: {
        placeholder: t(csvHeadersMap.clientDocument),
        required: true,
      }
    },
    {
      key: 'clientEmail',
      component: Input,
      label: t(csvHeadersMap.clientEmail),
      type: 'email',
      props: {
        placeholder: t(csvHeadersMap.clientEmail),
        required: true,
      }
    },
    {
      key: 'phone',
      component: TelInput,
      label: t(csvHeadersMap.phone),
      props: {
        ...telInputProps,
        placeholder: t(csvHeadersMap.phone),
        required: true,
      }
    },
  ];
};
